import React, { useEffect, useState } from 'react';
import { Sidebar, Header } from './components/Shell.jsx';
import { setActiveCurrency } from './data.js';
import { api } from './services/api.js';
import { useCollection } from './useCollection.js';
import {
  fromApiAccount, toApiAccount, fromApiContact, toApiContact,
  fromApiDeal, toApiDeal, fromApiPartner, toApiPartner,
  fromApiTask, toApiTask, fromApiProduct, toApiProduct,
  fromApiTeamMember, toApiTeamMember, fromApiWorkspace, fromApiNotification,
} from './adapters.js';
import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Leads from './pages/Leads.jsx';
import Partners from './pages/Partners.jsx';
import Accounts from './pages/Accounts.jsx';
import Products from './pages/Products.jsx';
import Tasks from './pages/Tasks.jsx';
import Reports from './pages/Reports.jsx';
import Settings from './pages/Settings.jsx';
import DealDetail from './pages/DealDetail.jsx';

const DEFAULT_WORKSPACE = { name: 'SCODAC', currency: 'USD', notifications: { dealStage: true, overdue: true, taskDue: true, weeklyDigest: false } };

export default function App() {
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [active, setActive] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [workspace, setWorkspaceState] = useState(DEFAULT_WORKSPACE);

  useEffect(() => {
    api.auth.me()
      .then((user) => setCurrentUser(fromApiTeamMember(user)))
      .catch(() => setCurrentUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  const authed = Boolean(currentUser);

  const accountsApi = useCollection(api.accounts, fromApiAccount, authed);
  const dealsApi = useCollection(api.deals, fromApiDeal, authed);
  const partnersApi = useCollection(api.partners, fromApiPartner, authed);
  const productsApi = useCollection(api.products, fromApiProduct, authed);
  const tasksApi = useCollection(api.tasks, fromApiTask, authed);
  const teamApi = useCollection(api.team, fromApiTeamMember, authed);
  const notificationsApi = useCollection(api.notifications, fromApiNotification, authed);

  const markNotificationRead = async (id) => {
    const updated = await api.notifications.markRead(id);
    notificationsApi.setItems((prev) => prev.map((n) => (n.id === id ? fromApiNotification(updated) : n)));
  };
  const markAllNotificationsRead = async () => {
    await api.notifications.markAllRead();
    notificationsApi.setItems((prev) => prev.map((n) => ({ ...n, unread: false })));
  };

  const contacts = accountsApi.items.flatMap((a) => a.contacts || []);

  useEffect(() => {
    if (!authed) return;
    api.settings.get().then((data) => {
      const ws = fromApiWorkspace(data);
      setWorkspaceState(ws);
      setActiveCurrency(ws.currency);
    }).catch(() => {});
  }, [authed]);

  const login = (user) => setCurrentUser(fromApiTeamMember(user));
  const logout = async () => {
    try { await api.auth.logout(); } catch { /* already logged out */ }
    setCurrentUser(null);
  };

  const navigate = (key) => { setActive(key); setSelectedDeal(null); };

  // -- accounts / contacts -------------------------------------------------
  const handleCreateAccount = async (form) => {
    const created = await accountsApi.create(toApiAccount(form));
    return created;
  };
  const handleUpdateAccount = async (id, form) => accountsApi.update(id, toApiAccount(form));

  const createContact = async (accountId, form) => {
    const created = fromApiContact(await api.contacts.create(accountId, toApiContact(form)));
    accountsApi.setItems((prev) => prev.map((a) => (a.id === accountId ? { ...a, contacts: [...(a.contacts || []), created] } : a)));
    return created;
  };
  const updateContact = async (accountId, contactId, form) => {
    const updated = fromApiContact(await api.contacts.update(accountId, contactId, toApiContact(form)));
    accountsApi.setItems((prev) => prev.map((a) => (a.id !== accountId ? a : {
      ...a,
      contacts: (a.contacts || []).map((c) => (c.id === contactId ? updated : (updated.primary ? { ...c, primary: false } : c))),
    })));
    return updated;
  };

  // -- deals ----------------------------------------------------------------
  const dealContext = { accounts: accountsApi.items, contacts, products: productsApi.items };
  const createDeal = async (form) => {
    const created = await dealsApi.create(toApiDeal(form, dealContext));
    return created;
  };
  const updateDeal = async (id, form) => dealsApi.update(id, toApiDeal(form, dealContext));
  const addDealNote = async (dealId, text) => {
    const deal = dealsApi.items.find((d) => d.id === dealId);
    const note = { id: `n${Date.now()}`, text, authorId: currentUser.id, createdAt: new Date().toISOString() };
    await dealsApi.update(dealId, { notes: [note, ...(deal?.notes || [])] });
  };

  // -- partners (name changes / deletion cascade into deal & account source) --
  const createPartner = async (form) => partnersApi.create(toApiPartner(form));
  const updatePartner = async (previous, form) => {
    const updated = await partnersApi.update(previous.id, toApiPartner(form));
    if (previous.name !== updated.name) {
      const affectedAccounts = accountsApi.items.filter((a) => a.source === previous.name);
      const affectedDeals = dealsApi.items.filter((d) => d.source === previous.name);
      await Promise.all([
        ...affectedAccounts.map((a) => accountsApi.update(a.id, { source: updated.name })),
        ...affectedDeals.map((d) => dealsApi.update(d.id, { source: updated.name })),
      ]);
    }
    return updated;
  };
  const deletePartner = async (partner) => {
    await partnersApi.remove(partner.id);
    const affectedAccounts = accountsApi.items.filter((a) => a.source === partner.name);
    const affectedDeals = dealsApi.items.filter((d) => d.source === partner.name);
    await Promise.all([
      ...affectedAccounts.map((a) => accountsApi.update(a.id, { source: 'Internal' })),
      ...affectedDeals.map((d) => dealsApi.update(d.id, { source: 'Internal' })),
    ]);
  };

  // -- products / tasks -------------------------------------------------------
  const createProduct = async (form) => productsApi.create(toApiProduct(form));
  const updateProduct = async (id, form) => productsApi.update(id, toApiProduct(form));

  const createTask = async (form) => tasksApi.create(toApiTask(form));
  const toggleTaskDone = async (id) => {
    const task = tasksApi.items.find((t) => t.id === id);
    if (!task) return;
    await tasksApi.update(id, { status: task.status === 'Done' ? 'Open' : 'Done' });
  };

  // -- team / workspace ---------------------------------------------------
  const createTeamMember = async (name) => {
    const email = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '.')}@scodac.com`;
    return teamApi.create(toApiTeamMember({ name, email, role: 'Team member', color: '#3D6EF5' }));
  };
  const updateTeamMember = async (id, form) => teamApi.update(id, toApiTeamMember(form));
  const removeTeamMember = async (member) => teamApi.remove(member.id);

  const saveWorkspace = async (patch) => {
    const data = await api.settings.update({ workspace: { name: patch.name, currency: patch.currency } });
    const ws = fromApiWorkspace(data);
    setWorkspaceState(ws);
    setActiveCurrency(ws.currency);
  };
  const saveNotifications = async (notifications) => {
    const data = await api.settings.update({ notifications });
    setWorkspaceState(fromApiWorkspace(data));
  };

  if (!authChecked) return <div className="app-loading">Loading…</div>;
  if (!authed) return <Login onLogin={login} />;

  const loadingCore = accountsApi.loading || dealsApi.loading || partnersApi.loading || teamApi.loading;
  if (loadingCore) return <div className="app-loading">Loading your workspace…</div>;

  let page;
  if (selectedDeal) {
    const deal = dealsApi.items.find((d) => d.id === selectedDeal.id) || selectedDeal;
    page = (
      <DealDetail
        deal={deal}
        currentUser={currentUser}
        accounts={accountsApi.items}
        contacts={contacts}
        team={teamApi.items}
        products={productsApi.items}
        onAddNote={(text) => addDealNote(deal.id, text)}
        onUpdateDeal={updateDeal}
        onBack={() => setSelectedDeal(null)}
        onOpenAccount={() => { setActive('accounts'); setSelectedDeal(null); }}
      />
    );
  } else {
    switch (active) {
      case 'leads':
        page = (
          <Leads
            deals={dealsApi.items} onCreateDeal={createDeal} onUpdateDeal={updateDeal} onOpenDeal={setSelectedDeal}
            partners={partnersApi.items} team={teamApi.items} products={productsApi.items} accounts={accountsApi.items} contacts={contacts}
          />
        );
        break;
      case 'partners':
        page = <Partners partners={partnersApi.items} deals={dealsApi.items} team={teamApi.items} onCreatePartner={createPartner} onUpdatePartner={updatePartner} onDeletePartner={deletePartner} />;
        break;
      case 'accounts':
        page = (
          <Accounts
            accounts={accountsApi.items} contacts={contacts} deals={dealsApi.items} team={teamApi.items} products={productsApi.items} partners={partnersApi.items}
            onCreateAccount={handleCreateAccount} onUpdateAccount={handleUpdateAccount}
            onCreateContact={createContact} onUpdateContact={updateContact}
            onCreateDeal={createDeal} onUpdateDeal={updateDeal} onCreateTeamMember={createTeamMember}
          />
        );
        break;
      case 'products':
        page = <Products products={productsApi.items} onCreateProduct={createProduct} onUpdateProduct={updateProduct} />;
        break;
      case 'tasks':
        page = <Tasks tasks={tasksApi.items} accounts={accountsApi.items} partners={partnersApi.items} team={teamApi.items} onCreateTask={createTask} onToggleDone={toggleTaskDone} />;
        break;
      case 'reports':
        page = <Reports deals={dealsApi.items} partners={partnersApi.items} products={productsApi.items} />;
        break;
      case 'settings':
        page = (
          <Settings
            workspace={workspace} onSaveWorkspace={saveWorkspace} onSaveNotifications={saveNotifications}
            team={teamApi.items} onCreateTeamMember={(form) => teamApi.create(toApiTeamMember(form))}
            onUpdateTeamMember={updateTeamMember} onRemoveTeamMember={removeTeamMember} currentUserId={currentUser.id}
          />
        );
        break;
      default:
        page = (
          <Dashboard
            deals={dealsApi.items} partners={partnersApi.items} accounts={accountsApi.items} tasks={tasksApi.items} team={teamApi.items}
            onOpenDeal={setSelectedDeal} onNavigate={navigate} currentUser={currentUser}
          />
        );
    }
  }

  return (
    <div className="app-shell">
      <Sidebar
        active={active}
        onNavigate={navigate}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        currentUser={currentUser}
        onLogout={logout}
        deals={dealsApi.items}
        partners={partnersApi.items}
        accounts={accountsApi.items}
      />
      <div className="app-main">
        <Header
          active={active} onMenu={() => setMobileOpen(true)} currentUser={currentUser} workspaceName={workspace.name}
          notifications={notificationsApi.items} onReloadNotifications={notificationsApi.reload}
          onMarkNotificationRead={markNotificationRead} onMarkAllNotificationsRead={markAllNotificationsRead}
        />
        <div className="app-content">{page}</div>
        <div className="copyright">© {new Date().getFullYear()} {workspace.name}. All rights reserved.</div>
      </div>
    </div>
  );
}
